// Annonces et sondages (D-028, D-062) : le « Diffuser un message » de Playbook, en plus large.
//  - à qui : tout le club, des rôles (coachs, bénévoles, parents, joueuses, bureau), des équipes,
//    des équipes engagées, des personnes ; un coach vise ses équipes ;
//  - sondage : une question, 2 à 8 choix ; ouvert un temps, ou clos à la main ;
//  - en grand à l'ouverture : « Compris » ou son vote, ou « Plus tard » (elle revient à la
//    prochaine ouverture) ;
//  - suivi : qui a répondu, qui a mis « plus tard », qui n'a pas vu ; relance ; résultats.
import {
  announcementAct, announcementPeople, announcementPreview, announcementPreviewBenevoles, announcementPrompts, announcementRemind, announcementResults,
  announcementTracking, archiveAnnouncement, clubAnnouncements, closeAnnouncement, markAnnouncementsRead, myAnnouncementReads,
  myAnnouncementStatus, postAnnouncement, type Announcement, type AnnStatus, type Audience, type Prompt, type Tracked,
} from '../lib/api';
import { markAnnouncementNotifsRead } from '../lib/notifications';
import { firstName, modOn, type Ctx } from './context';
import { moduleOfLink } from '../domain/modules';
import { agree } from '../domain/labels';
import { offerAnnouncementReach } from './reach';
import { $, $$, html, mount, openSheet, toast, type Html, armed } from './dom';

const ago = (iso: string) => {
  const min = Math.round((Date.now() - Date.parse(iso)) / 60000);
  if (min < 1) return 'à l\'instant';
  if (min < 60) return `il y a ${min} min`;
  const h = Math.round(min / 60);
  if (h < 24) return `il y a ${h} h`;
  return new Date(iso).toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' });
};
const until = (iso: string) => new Date(iso).toLocaleDateString('fr-FR', { weekday: 'long' });
const ROLE_LABEL: Record<string, string> = { coach: 'Coachs', volunteer: 'Bénévoles', supporter: 'Bénévoles', parent: 'Parents', player: 'Joueurs', admin: 'Bureau' };
/** « Joueurs », ou « Joueuses » si toutes les équipes du club sont féminines (D-008, D-103). */
const allGirls = (ctx: Ctx) => { const t = ctx.full.teams.filter(x => x.club_id === ctx.club?.id); return t.length > 0 && t.every(x => x.gender === 'F'); };
const roleLabel = (ctx: Ctx, r: string) => (r === 'player' ? agree(allGirls(ctx) ? 'F' : 'M', 'Joueurs', 'Joueuses') : ROLE_LABEL[r] ?? r);
/** Les puces du bureau : une seule « Bénévoles » (qui a pris un créneau ou est dans un groupe, et les bénévoles sans autre rôle). */
const ROLE_CHIPS = ['coach', 'benevole', 'parent', 'player', 'admin'] as const;
const BENEVOLE_ROLES = ['volunteer', 'supporter'] as const;

/** Ce que je peux viser : le bureau, tout le club ; un coach, ses équipes et leurs équipes engagées. */
function scope(ctx: Ctx) {
  const club = ctx.club;
  if (!club) return null;
  const admin = ctx.adminOf.some(c => c.id === club.id);
  const teams = ctx.teams.filter(t => t.club_id === club.id && (admin || ctx.staffOf.has(t.id)));
  const squads = ctx.squads.filter(q => teams.some(t => t.id === q.team_id) && ctx.staffOf.has(q.team_id));
  return { club, admin, teams, squads };
}
export const canAnnounce = (ctx: Ctx) => { const s = scope(ctx); return !!s && (s.admin || s.teams.length > 0); };

export interface Draft { team?: string | null; title?: string; body?: string; link?: string | null; audience?: Audience;
  /** Message d'un chef à son groupe de bénévoles (D-065) : public fixé, ce groupe seul. */
  group?: { id: string; name: string };
  /** Message à tous les chefs de groupe (D-070) : public fixé. */
  leads?: boolean }

/** L'audience en clair : « Tout le club », « Coachs + Bénévoles », « SF1 », « 3 personnes ». */
export function audienceLabel(ctx: Ctx, a: Announcement): string {
  if (!a.audience) return a.team_id ? ctx.teams.find(t => t.id === a.team_id)?.name ?? 'Équipe' : 'Tout le club';
  const au = a.audience;
  if (au.all) return 'Tout le club';
  return [...new Set((au.roles ?? []).map(r => roleLabel(ctx, r))), ...(au.teams ?? []).map(id => ctx.teams.find(t => t.id === id)?.name ?? 'Équipe'),
    ...(au.squads ?? []).map(id => ctx.squads.find(q => q.id === id)?.name ?? 'Équipe'),
    ...(au.users?.length ? [`${au.users.length} ${au.users.length > 1 ? 'personnes' : 'personne'}`] : []),
    ...(au.groups?.length ? [au.groups.length > 1 ? `${au.groups.length} groupes de bénévoles` : 'Groupe de bénévoles'] : []),
    ...(au.leads ? ['Chefs de groupe'] : [])].join(' + ') || '—';
}

const empty = (a: Audience) => !a.all && !a.roles?.length && !a.teams?.length && !a.squads?.length && !a.users?.length && !a.groups?.length && !a.leads;

/** Fiche de rédaction. `draft` : un message déjà préparé (relance des bénévoles…). */
export function openCompose(ctx: Ctx, onSent: () => void, draft: Draft = {}): void {
  const sc = scope(ctx);
  const group = draft.group ?? null, leads = !!draft.leads;
  if (!sc || (!group && !leads && !canAnnounce(ctx))) return;
  const { club, admin, teams, squads } = sc;
  let kind: 'message' | 'poll' = 'message';
  let aud: Audience = group ? { groups: [group.id] } : leads ? { leads: true } : draft.audience ?? (draft.team ? { teams: [draft.team] } : admin ? { all: true } : { teams: [teams[0]!.id] });
  const options = ['', ''];
  let people: { user_id: string; name: string }[] | null = null;
  const signed = group ? `${firstName(ctx) || 'Le chef'} · ${group.name.replace(/^\S+\s/, '')}` : leads && !admin ? `${firstName(ctx) || 'Un chef'} · chef de groupe` : admin ? 'Le bureau' : `Coach ${teams[0]?.name ?? ''}`.trim();

  openSheet(group ? `Message · ${group.name}` : leads ? 'Message aux chefs de groupe' : 'Nouvelle annonce', html`
    <form class="form" id="f-ann" novalidate>
      <div class="seg" role="radiogroup" aria-label="Type">
        <button type="button" role="radio" data-kind="message" aria-pressed="true">Message</button>
        <button type="button" role="radio" data-kind="poll" aria-pressed="false">Sondage</button>
      </div>
      <div class="field"><span>À qui</span><div class="ac-to" id="ac-to"></div><p class="hint" id="ac-count" aria-live="polite"></p></div>
      <label class="field"><span id="ac-title-l">Titre</span><input name="title" maxlength="120" required value="${draft.title ?? ''}" placeholder="ex. Maillots blancs samedi"></label>
      <label class="field" id="ac-body"><span>Message</span><textarea name="body" rows="5" maxlength="4000" placeholder="Le détail, si besoin.">${draft.body ?? ''}</textarea></label>
      <div class="field" id="ac-poll" hidden><span>Les choix</span><div class="ac-opts" id="ac-opts"></div>
        <button type="button" class="btn btn-link btn-sm" id="ac-add">+ Ajouter un choix</button>
        <label class="check"><input type="checkbox" name="multi"><span>Plusieurs choix possibles</span></label></div>
      <label class="check"><input type="checkbox" name="prompt" checked><span><strong>S'affiche en grand à l'ouverture</strong><br><span class="muted">Chacun répond, ou met « Plus tard » : elle revient à la prochaine ouverture.</span></span></label>
      <div class="row-2">
        <label class="field"><span>Ouvert pendant</span><select name="days">${[[3, '3 jours'], [7, '7 jours'], [14, '14 jours'], [30, '30 jours'], [0, 'Sans limite']].map(([v, t]) => html`<option value="${v}" ${v === 7 ? 'selected' : ''}>${t}</option>`)}</select></label>
        <label class="field"><span>Signé</span><input name="author" maxlength="60" value="${signed}"></label>
      </div>
      ${admin ? html`<label class="check" id="ac-public"><input type="checkbox" name="public"><span><strong>Aussi sur la page publique</strong> <span class="muted small">« Actualités » ; pas de nom ni de photo de mineur sans l'accord des parents</span></span></label>` : ''}
      <p class="form-msg" role="alert"></p>
      <button class="btn btn-primary" type="submit" id="ac-send">Envoyer</button>
    </form>`, (sheet, close) => {
    const f = $<HTMLFormElement>(sheet, '#f-ann')!;
    let timer = 0;
    const count = () => {
      clearTimeout(timer);
      timer = window.setTimeout(async () => {
        const out = $(sheet, '#ac-count')!;
        if (empty(aud)) { out.textContent = 'Choisis au moins un public.'; return; }
        const byRole = !group && !leads && !!(aud.all || aud.roles?.length);
        const [p, vol] = await Promise.all([announcementPreview(club.id, aud).catch(() => null),
          byRole ? announcementPreviewBenevoles(club.id, aud).catch(() => null) : Promise.resolve(null)]);
        if (!p) { out.textContent = ''; return; }
        if (group || leads) { out.textContent = `→ ${p.total} ${p.total > 1 ? 'personnes' : 'personne'}${group ? ' du groupe' : ' : les chefs de groupe'}`; $(sheet, '#ac-send')!.textContent = `Envoyer (${p.total})`; return; }
        const g = allGirls(ctx) ? 'F' : 'M';
        const parts = ((!byRole ? [] : [[p.player, agree(g, 'joueur', 'joueuse'), agree(g, 'joueurs', 'joueuses')], [p.parent, 'parent', 'parents'], [p.coach, 'coach', 'coachs'],
          [vol ?? p.volunteer, 'bénévole', 'bénévoles']]) as [number, string, string][])
          .filter(([n]) => n > 0).map(([n, one, many]) => `${n} ${n > 1 ? many : one}`);
        out.textContent = `→ ${p.total} ${p.total > 1 ? 'personnes' : 'personne'}${parts.length ? ` : ${parts.join(', ')}` : ''}${parts.length > 1 ? ' (chacune comptée une fois)' : ''}`;
        $(sheet, '#ac-send')!.textContent = p.total ? `Envoyer à ${p.total} ${p.total > 1 ? 'personnes' : 'personne'}` : 'Envoyer';
      }, 250);
    };
    const chip = (key: string, label: string, on: boolean) => html`<button type="button" class="ac-chip ${on ? 'on' : ''}" data-to="${key}" aria-pressed="${String(on)}">${label}</button>`;
    const drawTo = () => {
      const has = (k: 'roles' | 'teams' | 'squads', v: string) => ((aud[k] as string[] | undefined) ?? []).includes(v);
      if (group || leads) { mount($(sheet, '#ac-to')!, html`<span class="ac-chip on">${group ? group.name : 'Tous les chefs de groupe'}</span>`); count(); return; }
      mount($(sheet, '#ac-to')!, html`
        ${admin ? html`${chip('all', 'Tout le club', !!aud.all)}${ROLE_CHIPS.filter(r => r !== 'benevole' || modOn(ctx, 'benevolat')).map(r => r === 'benevole'
          ? chip('role:benevole', 'Bénévoles', BENEVOLE_ROLES.some(x => has('roles', x))) : chip(`role:${r}`, roleLabel(ctx, r), has('roles', r)))}` : ''}
        ${teams.map(t => chip(`team:${t.id}`, t.name, has('teams', t.id)))}
        ${squads.length > 1 || (squads.length && !admin) ? squads.map(q => chip(`squad:${q.id}`, q.name, has('squads', q.id))) : ''}
        ${chip('people', aud.users?.length ? `${aud.users.length} ${aud.users.length > 1 ? 'personnes choisies' : 'personne choisie'}` : 'Choisir des personnes…', !!aud.users?.length)}`);
      const pub = $(sheet, '#ac-public');
      if (pub) pub.hidden = kind !== 'message' || !aud.all;
      count();
    };
    const toggle = (k: 'roles' | 'teams' | 'squads', v: string) => {
      const list = new Set((aud[k] as string[] | undefined) ?? []);
      if (list.has(v)) list.delete(v); else list.add(v);
      aud = { ...aud, all: false, [k]: [...list] };
    };
    $(sheet, '#ac-to')!.addEventListener('click', async e => {
      const b = (e.target as HTMLElement).closest<HTMLButtonElement>('[data-to]');
      if (!b) return;
      const key = b.dataset.to!;
      if (key === 'all') aud = aud.all ? {} : { all: true };
      else if (key === 'role:benevole') {   // une puce, deux publics de la base (D-103)
        const on = BENEVOLE_ROLES.some(x => (aud.roles ?? []).includes(x));
        const rest = (aud.roles ?? []).filter(x => !(BENEVOLE_ROLES as readonly string[]).includes(x));
        aud = { ...aud, all: false, roles: on ? rest : [...rest, ...BENEVOLE_ROLES] };
      }
      else if (key.startsWith('role:')) toggle('roles', key.slice(5));
      else if (key.startsWith('team:')) toggle('teams', key.slice(5));
      else if (key.startsWith('squad:')) toggle('squads', key.slice(6));
      else if (key === 'people') {
        people ??= await announcementPeople(club.id).catch(() => []);
        openPeoplePicker(people, aud.users ?? [], users => { aud = { ...aud, all: false, users }; drawTo(); });
        return;
      }
      drawTo();
    });
    const drawOpts = () => {
      mount($(sheet, '#ac-opts')!, html`${options.map((o, i) => html`<div class="ac-opt"><span aria-hidden="true">○</span>
        <input data-opt="${i}" maxlength="80" value="${o}" placeholder="Choix ${i + 1}" aria-label="Choix ${i + 1}">
        ${options.length > 2 ? html`<button type="button" class="link-x" data-optrm="${i}" aria-label="Retirer le choix ${i + 1}">retirer</button>` : ''}</div>`)}`);
      $$<HTMLInputElement>(sheet, '[data-opt]').forEach(inp => inp.addEventListener('input', () => { options[Number(inp.dataset.opt)] = inp.value; }));
      $$<HTMLButtonElement>(sheet, '[data-optrm]').forEach(b => b.addEventListener('click', () => { options.splice(Number(b.dataset.optrm), 1); drawOpts(); }));
      ($(sheet, '#ac-add') as HTMLButtonElement).disabled = options.length >= 8;
    };
    $(sheet, '#ac-add')!.addEventListener('click', () => { options.push(''); drawOpts(); });
    for (const b of $$<HTMLButtonElement>(sheet, '[data-kind]')) b.addEventListener('click', () => {
      kind = b.dataset.kind === 'poll' ? 'poll' : 'message';
      $$(sheet, '[data-kind]').forEach(x => x.setAttribute('aria-pressed', String(x === b)));
      $(sheet, '#ac-poll')!.hidden = kind !== 'poll';
      $(sheet, '#ac-body')!.hidden = kind === 'poll';
      $(sheet, '#ac-title-l')!.textContent = kind === 'poll' ? 'La question' : 'Titre';
      (f.elements.namedItem('title') as HTMLInputElement).placeholder = kind === 'poll' ? 'ex. Quel créneau pour la réunion ?' : 'ex. Maillots blancs samedi';
      drawTo();
    });
    drawOpts(); drawTo();

    f.addEventListener('submit', async e => {
      e.preventDefault();
      const d = new FormData(f), msg = (t: string) => { $(f, '.form-msg')!.textContent = t; };
      const title = String(d.get('title') ?? '').trim();
      if (empty(aud)) { msg('Choisis à qui l’envoyer.'); return; }
      if (!title) { msg(kind === 'poll' ? 'Écris la question.' : 'Donne un titre à l’annonce.'); return; }
      const opts = options.map(o => o.trim()).filter(Boolean);
      if (kind === 'poll' && opts.length < 2) { msg('Au moins deux choix.'); return; }
      if (kind === 'poll' && new Set(opts.map(o => o.toLowerCase())).size !== opts.length) { msg('Deux choix identiques.'); return; }
      const days = Number(d.get('days'));
      try {
        await postAnnouncement({
          club_id: club.id, team_id: null, author_label: String(d.get('author') ?? '').trim() || 'Le club', title,
          body: kind === 'message' ? String(d.get('body') ?? '').trim() || null : null, link: draft.link ?? null,
          is_public: kind === 'message' && !!aud.all && d.get('public') === 'on',
          kind, poll_options: kind === 'poll' ? opts : null, poll_multi: kind === 'poll' && d.get('multi') === 'on',
          prompt: d.get('prompt') === 'on', closes_at: days ? new Date(Date.now() + days * 86400_000).toISOString() : null, audience: aud,
        });
        close(); toast(kind === 'poll' ? 'Sondage envoyé' : 'Annonce envoyée'); onSent();
        // Ceux qu'elle ne touche pas (pas encore dans l'app) : « N sans l'app · partager sur WhatsApp » (D-103).
        void offerAnnouncementReach(ctx, club.id, aud, title, kind === 'message' ? String(d.get('body') ?? '').trim() || null : null);
      } catch { msg('Envoi impossible. Réessaie.'); }
    });
  });
}

/** Choisir des personnes (comptes du club, ou de mes équipes pour un coach). */
function openPeoplePicker(all: { user_id: string; name: string }[], chosen: string[], onDone: (users: string[]) => void) {
  const sel = new Set(chosen);
  openSheet('Choisir des personnes', html`
    <input class="search" type="search" placeholder="Chercher un prénom" aria-label="Chercher">
    <ul class="pick-list">${all.map(p => html`<li><label class="check ac-person" data-q="${p.name.toLowerCase()}"><input type="checkbox" value="${p.user_id}" ${sel.has(p.user_id) ? 'checked' : ''}><span>${p.name}</span></label></li>`)}</ul>
    ${all.length ? '' : html`<p class="muted">Personne à choisir pour l’instant.</p>`}
    <button type="button" class="btn btn-primary" id="pp-ok">Valider</button>`, (sheet, close) => {
    const s = $<HTMLInputElement>(sheet, '.search')!;
    s.addEventListener('input', () => { const q = s.value.trim().toLowerCase(); $$(sheet, '[data-q]').forEach(l => { l.parentElement!.hidden = !!q && !l.dataset.q!.includes(q); }); });
    $$<HTMLInputElement>(sheet, 'input[type=checkbox]').forEach(c => c.addEventListener('change', () => { if (c.checked) sel.add(c.value); else sel.delete(c.value); }));
    $(sheet, '#pp-ok')!.addEventListener('click', () => { close(); onDone([...sel]); });
  });
}

/** Les barres d'un sondage. */
const bars = (opts: string[], r: { counts: number[]; mine: number[] | null }): Html => {
  const max = Math.max(1, ...r.counts);
  const top = Math.max(...r.counts);
  return html`<div class="poll-bars">${opts.map((o, i) => html`<div class="poll-row ${r.mine?.includes(i) || ((r.counts[i] ?? 0) === top && top > 0) ? 'mine' : ''}">
    <span class="poll-label"><strong>${o}</strong><b>${r.counts[i] ?? 0}</b></span>
    <span class="poll-bar"><span style="width:${Math.round(100 * (r.counts[i] ?? 0) / max)}%"></span></span></div>`)}</div>`;
};

const isOpen = (a: { closed_at: string | null; closes_at: string | null }) => !a.closed_at && (!a.closes_at || Date.parse(a.closes_at) > Date.now());

/** Écran des annonces. Les annonces affichées sont marquées vues. */
export async function renderAnnouncements(root: HTMLElement, ctx: Ctx, focusId?: string, track = false): Promise<void> {
  const club = ctx.club;
  if (!club) { mount(root, html`<div class="empty"><p class="empty-title">Aucun club.</p></div>`); return; }
  if (focusId && track) { await renderTracking(root, ctx, focusId); return; }
  // D-122 : une annonce qui mène à un module éteint (« Bravo à… », une relance de bénévoles) ne paraît plus.
  const list = (await clubAnnouncements(club.id)).filter(a => !offModule(ctx, a.link) || a.id === focusId);
  const [reads, mine] = await Promise.all([myAnnouncementReads(list.map(a => a.id)), myAnnouncementStatus(list.map(a => a.id)).catch(() => [])]);
  const read = new Set(reads.map(r => r.announcement_id));
  const st = new Map(mine.map(m => [m.announcement_id, m]));
  const tracker = (a: Announcement) => a.author_id === ctx.uid || ctx.adminOf.some(c => c.id === a.club_id) || (a.team_id !== null && ctx.staffOf.has(a.team_id));
  const ordered = focusId ? [...list.filter(a => a.id === focusId), ...list.filter(a => a.id !== focusId)] : list;

  mount(root, html`
    <section class="screen">
      <header class="screen-head head-row">
        <div><p class="eyebrow">${club.name}</p><h2>Annonces</h2></div>
        ${canAnnounce(ctx) ? html`<button type="button" class="btn btn-primary btn-sm" id="b-ann" data-guide="ann-new">+ Annonce</button>` : ''}
      </header>
      ${ordered.length ? ordered.map(a => {
        const me = st.get(a.id);
        const fresh = a.author_id !== ctx.uid && (me ? me.status === 'pending' : !read.has(a.id));
        const canVote = a.kind === 'poll' && !!me && me.status !== 'done' && isOpen(a);
        return html`
        <article class="ann ${fresh ? 'ann-new' : ''} ${a.id === focusId ? 'ann-focus' : ''}" data-guide="ann-item" data-ann="${a.id}">
          <p class="eyebrow">${a.kind === 'poll' ? `Sondage${isOpen(a) ? '' : ' clos'} · ` : ''}${a.author_label} · ${audienceLabel(ctx, a)} · ${ago(a.created_at)}</p>
          <h3 class="ann-title">${a.title}</h3>
          ${a.body ? html`<p class="ann-body">${a.body}</p>` : ''}
          ${canVote ? html`<form class="ann-vote" data-vote="${a.id}" novalidate>${a.poll_options!.map((o, i) => html`
              <label class="ann-choice"><input type="${a.poll_multi ? 'checkbox' : 'radio'}" name="c" value="${i}"><span>${o}</span></label>`)}
              <button class="btn btn-primary btn-sm" type="submit">Voter</button>
              ${a.closes_at ? html`<span class="muted small">Réponse avant ${until(a.closes_at)}</span>` : ''}</form>`
            : a.kind === 'poll' ? html`<div data-results="${a.id}"></div>` : ''}
          <div class="ann-foot">
            ${a.link ? html`<a class="btn btn-ghost btn-sm" href="${a.link}">Ouvrir</a>` : ''}
            ${a.kind === 'message' && a.prompt && me && me.status !== 'done' ? html`<button type="button" class="btn btn-ghost btn-sm" data-ok="${a.id}">Compris</button>` : ''}
            ${tracker(a) ? html`<a class="btn btn-link btn-sm" href="#/annonces/${a.id}/suivi" data-sum="${a.id}">Suivi</a>` : ''}
            ${a.author_id === ctx.uid ? html`<button type="button" class="link-x" data-archive="${a.id}">retirer</button>` : ''}
          </div>
        </article>`;
      }) : html`
        <div class="empty"><p class="empty-title">Aucune annonce pour l'instant.</p>
          <p>${canAnnounce(ctx) ? 'Écris la première avec « + Annonce » : chacun la reçoit sur son téléphone.' : 'Les messages du club et de ton équipe arriveront ici.'}</p></div>`}
    </section>`);

  const reload = () => renderAnnouncements(root, ctx, focusId);
  $(root, '#b-ann')?.addEventListener('click', () => openCompose(ctx, () => void reload()));
  for (const el of $$(root, '[data-results]')) {
    const a = list.find(x => x.id === el.dataset.results)!;
    void announcementResults(a.id).then(r => { if (r) mount(el, html`${bars(a.poll_options!, r)}<p class="muted small">${r.voters} ${r.voters > 1 ? 'ont voté' : 'a voté'} sur ${r.total}</p>`); }).catch(() => undefined);
  }
  for (const el of $$<HTMLAnchorElement>(root, '[data-sum]')) {
    void announcementTracking(el.dataset.sum!).then(t => {
      if (!t.length) return;
      const done = t.filter(x => x.status === 'done').length, later = t.filter(x => x.status === 'later').length;
      el.textContent = `Suivi · ${done}/${t.length} ${list.find(a => a.id === el.dataset.sum)?.kind === 'poll' ? 'ont voté' : 'ont répondu'}${later ? ` · ${later} plus tard` : ''}`;
    }).catch(() => undefined);
  }
  for (const fm of $$<HTMLFormElement>(root, '[data-vote]')) fm.addEventListener('submit', async e => {
    e.preventDefault();
    const choice = new FormData(fm).getAll('c').map(Number);
    if (!choice.length) { toast('Choisis une réponse.', 'ko'); return; }
    try { await announcementAct(fm.dataset.vote!, 'done', choice); toast('Vote enregistré'); await reload(); } catch { toast('Vote impossible (sondage clos ?).', 'ko'); }
  });
  for (const b of $$<HTMLButtonElement>(root, '[data-ok]')) b.addEventListener('click', async () => {
    try { await announcementAct(b.dataset.ok!, 'done'); await reload(); } catch { toast('Impossible pour le moment.', 'ko'); }
  });
  for (const b of $$<HTMLButtonElement>(root, '[data-archive]')) {
    b.addEventListener('click', async () => {
      if (!armed(b, 'Confirmer : retirer l’annonce')) return;
      try { await archiveAnnouncement(b.dataset.archive!); toast('Annonce retirée'); await reload(); }
      catch { toast('Impossible pour le moment.', 'ko'); }
    });
  }
  void markAnnouncementsRead(list.filter(a => !read.has(a.id) && a.author_id !== ctx.uid).map(a => a.id)).catch(() => undefined);
  await markAnnouncementNotifsRead(list.map(a => a.id)).catch(() => undefined);
}

const STATUS: Record<AnnStatus, [string, string]> = { done: ['Compris', 'chip-ok'], later: ['Plus tard', 'chip-warn'], seen: ['Vu', 'chip-mute'], pending: ['Pas vu', 'chip-ko'] };

/** Le suivi d'une annonce : résultats, qui a répondu, qui a mis « plus tard », qui n'a pas vu. */
async function renderTracking(root: HTMLElement, ctx: Ctx, id: string): Promise<void> {
  const list = await clubAnnouncements(ctx.club!.id);
  const a = list.find(x => x.id === id);
  const [t, r] = await Promise.all([announcementTracking(id).catch(() => [] as Tracked[]), a?.kind === 'poll' ? announcementResults(id).catch(() => null) : Promise.resolve(null)]);
  if (!a || !t.length) { mount(root, html`<section class="screen"><a class="back" href="#/annonces">‹ Annonces</a><div class="empty"><p class="empty-title">Suivi indisponible.</p></div></section>`); return; }
  const done = t.filter(x => x.status === 'done').length, later = t.filter(x => x.status === 'later').length, unseen = t.filter(x => x.status === 'pending').length;
  const late = t.filter(x => x.status !== 'done').length;
  const short = (c: number[] | null) => (c ?? []).map(i => a.poll_options?.[i] ?? '?').join(', ');
  mount(root, html`
    <section class="screen">
      <div class="pp-nav"><a class="back" href="#/annonces">‹ Annonces</a>
        <span class="chip ${isOpen(a) ? 'chip-ok' : 'chip-mute'}">${isOpen(a) ? (a.closes_at ? `Ouvert jusqu’à ${until(a.closes_at)}` : 'Ouvert') : 'Clos'}</span></div>
      <header class="screen-head"><p class="eyebrow">${a.kind === 'poll' ? 'Sondage' : 'Annonce'} · ${audienceLabel(ctx, a)} · ${t.length} ${t.length > 1 ? 'personnes' : 'personne'}</p><h2>${a.title}</h2></header>
      ${r && a.poll_options ? html`<section class="block">${bars(a.poll_options, r)}</section>` : ''}
      <section class="block at-counts">
        <div><b class="at-ok">${done}</b><span>${a.kind === 'poll' ? 'ont voté' : 'compris'}</span></div>
        <div><b class="at-warn">${later}</b><span>plus tard</span></div>
        <div><b class="at-ko">${unseen}</b><span>pas vu</span></div>
      </section>
      <section class="block"><ul class="at-list">${t.map(x => html`<li>
        <span class="at-init" aria-hidden="true">${x.name.charAt(0).toUpperCase()}</span><span class="at-name">${x.name}</span>
        <span class="chip ${STATUS[x.status][1]}">${x.status === 'done' && a.kind === 'poll' ? short(x.choice) : STATUS[x.status][0]}</span></li>`)}</ul></section>
      <div class="btn-row">
        ${a.kind === 'poll' && isOpen(a) ? html`<button type="button" class="btn btn-ghost" id="at-close">Clore le sondage</button>` : ''}
        ${late && isOpen(a) ? html`<button type="button" class="btn btn-primary" id="at-remind">Relancer ${late > 1 ? `les ${late}` : 'la personne'}</button>` : ''}
      </div>
    </section>`);
  $(root, '#at-remind')?.addEventListener('click', async e => {
    (e.currentTarget as HTMLButtonElement).disabled = true;
    try { const n = await announcementRemind(id); toast(n ? `Relance envoyée (${n})` : 'Déjà relancées aujourd’hui'); } catch { toast('Impossible pour le moment.', 'ko'); }
  });
  $(root, '#at-close')?.addEventListener('click', async () => { try { await closeAnnouncement(id); toast('Sondage clos'); await renderTracking(root, ctx, id); } catch { toast('Impossible pour le moment.', 'ko'); } });
}

/** « Plus tard » : on ne la remontre pas avant la prochaine ouverture de l'app. */
const laterThisSession = new Set<string>();

/** À l'ouverture : les annonces à afficher en grand, une à une. */
export async function showAnnouncementPrompts(): Promise<void> {
  if (document.querySelector('.annp-back')) return;
  const queue = (await announcementPrompts().catch(() => [] as Prompt[])).filter(p => !laterThisSession.has(p.id));
  if (!queue.length) return;
  const host = document.createElement('div');
  host.className = 'annp-back';
  document.body.append(host);
  let i = 0;
  const next = () => { i++; if (i >= queue.length) host.remove(); else draw(); };
  const draw = () => {
    const p = queue[i]!;
    mount(host, html`
      <section class="annp" role="dialog" aria-modal="true" aria-label="${p.kind === 'poll' ? 'Sondage' : 'Annonce'}">
        <div class="annp-top"><span class="annp-av" aria-hidden="true">${(p.author || p.author_label).charAt(0).toUpperCase()}</span>
          <span class="annp-who"><strong>${p.author}</strong><small>${p.author_label}</small></span>
          ${queue.length > 1 ? html`<span class="annp-n">${i + 1} / ${queue.length}</span>` : ''}</div>
        <p class="annp-eyebrow">${p.kind === 'poll' ? `Sondage${p.closes_at ? ` · réponse avant ${until(p.closes_at)}` : ''}` : 'Annonce'}</p>
        <h2 class="annp-title">${p.title}</h2>
        ${p.body ? html`<p class="annp-body">${p.body}</p>` : ''}
        ${p.kind === 'poll' ? html`<div class="annp-choices" role="${p.poll_multi ? 'group' : 'radiogroup'}">${p.poll_options!.map((o, k) => html`
          <label class="annp-choice"><input type="${p.poll_multi ? 'checkbox' : 'radio'}" name="annp" value="${k}"><span>${o}</span></label>`)}</div>` : ''}
        <button type="button" class="btn btn-primary annp-go" id="annp-go">${p.kind === 'poll' ? 'Voter' : 'Compris'}</button>
        <button type="button" class="btn btn-link" id="annp-later">Plus tard</button>
        <p class="annp-hint">« Plus tard » : elle reviendra à la prochaine ouverture de l'app.</p>
      </section>`);
    $(host, '#annp-go')!.addEventListener('click', async () => {
      const choice = $$<HTMLInputElement>(host, 'input[name=annp]').filter(x => x.checked).map(x => Number(x.value));
      if (p.kind === 'poll' && !choice.length) { toast('Choisis une réponse.', 'ko'); return; }
      try { await announcementAct(p.id, 'done', p.kind === 'poll' ? choice : null); if (p.kind === 'poll') toast('Vote enregistré'); next(); }
      catch { toast('Impossible pour le moment.', 'ko'); }
    });
    $(host, '#annp-later')!.addEventListener('click', () => { laterThisSession.add(p.id); void announcementAct(p.id, 'later').catch(() => undefined); next(); });
  };
  draw();
}

/** Une annonce pour les bénévoles seulement : visant les bénévoles, les supporters, des groupes ou leurs chefs,
 *  ou une relance de bénévolat (lien vers le bénévolat). */
const forVolunteers = (a: Announcement) => {
  const au = a.audience;
  if (a.link?.startsWith('#/benevoles')) return true;
  if (!au || au.all || a.team_id || au.teams?.length || au.squads?.length || au.users?.length) return false;
  return !!(au.groups?.length || au.leads || (au.roles?.length && au.roles.every(r => r === 'volunteer' || r === 'supporter')));
};
/** Une annonce qui mène à un module que le club a éteint (D-122). */
const offModule = (ctx: Ctx, link: string | null | undefined) => { const m = moduleOfLink(link); return !!m && !modOn(ctx, m); };
/** Une annonce d'équipe : elle vise des équipes, pas tout le club. */
const forTeams = (a: Announcement) => !a.audience?.all && !!(a.team_id || a.audience?.teams?.length || a.audience?.squads?.length);

/** Carte d'accueil : la dernière annonce non lue. Casquette Bénévole à part (D-097) : l'accueil d'un rôle
 *  garde les annonces qui le visent (`role`), celui de la casquette Bénévole celles du club (`benevole`). */
export async function renderAnnouncementCard(el: HTMLElement, ctx: Ctx, side?: 'role' | 'benevole'): Promise<void> {
  if (!ctx.club) return;
  const list = await clubAnnouncements(ctx.club.id).catch(() => [] as Announcement[]);
  const read = new Set((await myAnnouncementReads(list.map(a => a.id)).catch(() => [])).map(r => r.announcement_id));
  const a = list.find(x => !read.has(x.id) && x.author_id !== ctx.uid
    && !(side === 'role' && forVolunteers(x)) && !(side === 'benevole' && forTeams(x)) && !offModule(ctx, x.link));
  if (!a) { mount(el, html``); return; }
  mount(el, html`
    <a class="card ann-card" href="#/annonces/${a.id}">
      <span class="eyebrow eyebrow-accent">${a.kind === 'poll' ? 'Sondage' : 'Annonce'} · ${a.author_label} · ${ago(a.created_at)}</span>
      <span class="card-title">${a.title}</span>
      ${a.body ? html`<span class="ann-excerpt">${a.body}</span>` : ''}
    </a>`);
}
